import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Save, Loader2, Plus, Trash2, RefreshCw, CheckCircle2, AlertCircle, Info, Pencil, X,
} from "lucide-react";

/**
 * AdminInventory10VK — authoritative IMS for 10-Vial Kits (10VK).
 *
 * Mirrors the SUPPLY/SELECT IMS UI with kit-centric terminology:
 *   · VIALS → KITS
 *   · CPV   → CPK
 *   · Packaging / Processing Per Vial → Per Kit
 * CPK = Total Landed Cost ÷ Purchased Kits
 * Packaging total = Packaging/Processing Per Kit × Purchased Kits.
 *
 * Hits an isolated backend lane:
 *   GET    /api/admin/inventory/10vk/lots
 *   POST   /api/admin/inventory/10vk/lots
 *   PUT    /api/admin/inventory/10vk/lots/:id
 *   DELETE /api/admin/inventory/10vk/lots/:id
 *
 * Architecture:
 *   · IMS is authoritative. `products.kits_in_stock` is derived from the
 *     sum of active 10VK lots via `_sync_kits_from_lots`.
 *   · Sales deduct FIFO (oldest-first) on paid webhook / mark-paid.
 *   · Refund/cancel credits newest-first.
 *   · Segregated from SUPPLY / SELECT by `inventory_lots.product_tier == "10vk"`.
 */

const TESTING_LABELS = { untested: "Untested", tested: "Tested" };

const TAB_BTN_ACTIVE = "border-b-2 border-blue-600 text-blue-600";
const TAB_BTN_IDLE = "border-b-2 border-transparent text-slate-600 hover:text-slate-900";
const PRIMARY_BTN = "inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm ring-1 ring-blue-700/10";
const SECONDARY_BTN = "inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 hover:border-slate-400 transition-colors";
const GHOST_BTN = "inline-flex items-center gap-2 px-3 py-2 rounded-md text-slate-600 text-sm font-medium hover:bg-slate-100 transition-colors";
const DANGER_ICON_BTN = "inline-flex items-center justify-center w-9 h-9 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors";
const CARD = "bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)]";

export default function AdminInventory10VK() {
  const [subTab, setSubTab] = useState("lots");
  const [reloadKey, setReloadKey] = useState(0);
  const goToAdd = () => setSubTab("add");

  return (
    <div className="mt-8" data-testid="admin-inventory-10vk">
      {subTab === "add" && (
        <Add10VKForm
          onSaved={() => { setReloadKey((k) => k + 1); setSubTab("lots"); }}
          onCancel={() => setSubTab("lots")}
        />
      )}
      {subTab === "lots" && (
        <TenVKLotsTable key={reloadKey} onGoToAdd={goToAdd} />
      )}
    </div>
  );
}

/* ───────────────────────── Table ─────────────────────────────────── */

function TenVKLotsTable({ onGoToAdd }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/inventory/10vk/lots");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Failed to load 10VK inventory");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // CREATE PEC / EDIT PEC — mirrors the SUPPLY IMS pattern 1:1 but routes
  // to the 10VK PEC (not Supply). CREATE PEC when the lot has no live PEC
  // (pec_id null or pec_deleted true); EDIT PEC when a live PEC is linked.
  // Deep-link is handled by Admin.jsx's existing /?tab=products&... effect.
  const openPec = (r) => {
    if (!r?.pec_id) return;
    window.location.assign(`/admin?tab=products&edit=${encodeURIComponent(r.pec_id)}`);
  };
  const createPecFromLot = (r) => {
    if (!r?.lot_id) return;
    window.location.assign(`/admin?tab=products&create_from_10vk_lot=${encodeURIComponent(r.lot_id)}`);
  };

  const onDelete = async (r) => {
    const label = `${r.name || "—"} — ${r.supplier_name || "no vendor"}${r.batch_number ? ` / ${r.batch_number}` : ""}`;
    if (!window.confirm(`Delete this 10VK lot?\n\n${label}\n\nIMS is authoritative — removing this lot immediately subtracts its remaining kits from storefront availability.`)) return;
    try {
      await api.delete(`/admin/inventory/10vk/lots/${r.lot_id}`);
      toast.success("Lot deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Delete failed");
    }
  };

  return (
    <div className={CARD} data-testid="inventory-10vk-table">
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200 flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <h3 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="inventory-10vk-heading">
            Research 10-Vial Kits (10VK)
          </h3>
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider rounded bg-oxblood/10 text-oxblood ring-1 ring-oxblood/30">
            10VK
          </span>
          <span className="text-sm text-slate-500 font-medium">{rows.length} lots</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className={GHOST_BTN} data-testid="inventory-10vk-refresh">
            <RefreshCw size={14} strokeWidth={2} /> Refresh
          </button>
          <button onClick={onGoToAdd} className={PRIMARY_BTN} data-testid="inventory-10vk-add-lot">
            <Plus size={14} strokeWidth={2.5} /> Add Inventory
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            No 10VK inventory yet. Click{" "}
            <button onClick={onGoToAdd} className="underline font-semibold text-blue-700 hover:text-blue-800" data-testid="inventory-10vk-empty-add-inline">
              Add Inventory
            </button>{" "}to enter your first 10VK lot.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {["Product", "Kits", "CPK", "Lot / Batch", "Received", "Expiration", "Supplier", "Testing", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const inStock = Number(r.kits_in_stock || 0) > 0;
                const testId = `10vk-${r.lot_id.slice(-6)}`;
                const cpk = (() => {
                  const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
                  const pc = num(r.product_cost), tc = num(r.testing_cost), sf = num(r.shipping_freight), mc = num(r.misc_cost);
                  const ppk = num(r.packaging_processing_per_kit), qty = num(r.purchased_quantity);
                  const any = [pc, tc, sf, mc, ppk].some((v) => Number.isFinite(v));
                  if (!any || !Number.isFinite(qty) || qty <= 0) return "—";
                  const pk = Number.isFinite(ppk) ? ppk * qty : 0;
                  const lt = (Number.isFinite(pc) ? pc : 0) + (Number.isFinite(tc) ? tc : 0) + (Number.isFinite(sf) ? sf : 0) + (Number.isFinite(mc) ? mc : 0) + pk;
                  return `$${(lt / qty).toFixed(2)}`;
                })();
                return (
                  <tr key={r.lot_id} className="bg-white hover:bg-slate-100 transition-colors" data-testid={`inv-10vk-row-${testId}`}>
                    <td className="px-4 py-3.5 text-[14px] text-slate-900 font-semibold">{r.name || "—"}</td>
                    <td className="px-4 py-3.5 font-mono text-[14px] font-semibold text-slate-900" data-testid={`inv-10vk-kits-${testId}`}>{r.kits_in_stock}</td>
                    <td className="px-4 py-3.5 font-mono text-[13px] font-semibold text-slate-900 whitespace-nowrap" data-testid={`inv-10vk-cpk-${testId}`}>{cpk}</td>
                    <td className="px-4 py-3.5 text-[13px] font-mono text-slate-700">{r.batch_number || "—"}</td>
                    <td className="px-4 py-3.5 text-[13px] text-slate-700 whitespace-nowrap">{fmtDate(r.date_received)}</td>
                    <td className="px-4 py-3.5 text-[13px] text-slate-700 whitespace-nowrap">{fmtDate(r.expiration_date)}</td>
                    <td className="px-4 py-3.5 text-[13px] text-slate-800">{r.supplier_name || "—"}</td>
                    <td className="px-4 py-3.5 text-xs"><TestingBadge status={r.testing_status} /></td>
                    <td className="px-4 py-3.5"><StatusBadge inStock={inStock} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        {/* CREATE PEC when the lot has no live PEC
                            (pec_id null OR pec_deleted true). EDIT PEC
                            when a live PEC is linked. Mirrors Supply IMS. */}
                        {(() => {
                          const isLive = !!r.pec_id && !r.pec_deleted;
                          const label = isLive ? "EDIT PEC" : "CREATE PEC";
                          const onClick = () => (isLive ? openPec(r) : createPecFromLot(r));
                          return (
                            <button
                              onClick={onClick}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ring-1 transition-colors ${
                                isLive
                                  ? "bg-slate-50 text-slate-700 hover:bg-slate-100 ring-slate-300"
                                  : "bg-amber-50 text-amber-800 hover:bg-amber-100 ring-amber-300"
                              }`}
                              data-testid={`ims-10vk-pec-${testId}`}
                              title={isLive
                                ? "Open the linked 10VK PEC"
                                : "Create a 10VK PEC from this IMS entry"}
                            >
                              {label}
                            </button>
                          );
                        })()}
                        <button onClick={() => setEditing(r)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 hover:text-blue-800 ring-1 ring-blue-200 transition-colors" data-testid={`inv-10vk-edit-${testId}`}>
                          <Pencil size={12} strokeWidth={2.5} /> Edit
                        </button>
                        <button onClick={() => onDelete(r)} className={DANGER_ICON_BTN} data-testid={`inv-10vk-delete-${testId}`} title="Delete this lot" aria-label="Delete lot">
                          <Trash2 size={14} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <Edit10VKDialog lot={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
      )}
    </div>
  );
}

function fmtDate(v) { const s = String(v || "").trim(); return s ? s.slice(0, 10) : "—"; }
function TestingBadge({ status }) {
  const tested = String(status || "").toLowerCase() === "tested";
  return tested
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-medium ring-1 ring-green-200"><CheckCircle2 size={11} strokeWidth={2.5} /> Tested</span>
    : <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium ring-1 ring-slate-200">Untested</span>;
}
function StatusBadge({ inStock }) {
  return inStock
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-medium ring-1 ring-green-200"><CheckCircle2 size={11} strokeWidth={2.5} /> In stock</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium ring-1 ring-amber-200"><AlertCircle size={11} strokeWidth={2.5} /> Out</span>;
}

/* ───────────────────────── Add form ──────────────────────────────── */

function CostCard({ productCost, setProductCost, testingCost, setTestingCost, shippingFreight, setShippingFreight, miscCost, setMiscCost, packagingPerKit, setPackagingPerKit, purchased, prefix }) {
  const num = (v) => (v === "" || v === null || v === undefined) ? 0 : Number(v) || 0;
  const q = num(purchased);
  const packagingTotal = q * num(packagingPerKit);
  const total = num(productCost) + num(testingCost) + num(shippingFreight) + num(miscCost) + packagingTotal;
  const cpk = q > 0 ? total / q : 0;
  const fmt = (v) => `$${(v || 0).toFixed(2)}`;

  return (
    <FormCard title="4. Cost / CPK">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <FieldLabel required>Product Cost (USD)</FieldLabel>
            <Input type="number" min="0" step="0.01" value={productCost} onChange={(e) => setProductCost(e.target.value)} className="h-10 text-sm" placeholder="0.00" data-testid={`${prefix}-product-cost`} />
          </div>
          <div>
            <FieldLabel>Testing Cost (USD)</FieldLabel>
            <Input type="number" min="0" step="0.01" value={testingCost} onChange={(e) => setTestingCost(e.target.value)} className="h-10 text-sm" placeholder="0.00" data-testid={`${prefix}-testing-cost`} />
          </div>
          <div>
            <FieldLabel>Shipping / Freight (USD)</FieldLabel>
            <Input type="number" min="0" step="0.01" value={shippingFreight} onChange={(e) => setShippingFreight(e.target.value)} className="h-10 text-sm" placeholder="0.00" data-testid={`${prefix}-shipping`} />
          </div>
          <div>
            <FieldLabel>Misc. Cost (USD)</FieldLabel>
            <Input type="number" min="0" step="0.01" value={miscCost} onChange={(e) => setMiscCost(e.target.value)} className="h-10 text-sm" placeholder="0.00" data-testid={`${prefix}-misc-cost`} />
          </div>
          <div>
            <FieldLabel>Packaging / Processing Per Kit (USD)</FieldLabel>
            <Input type="number" min="0" step="0.01" value={packagingPerKit} onChange={(e) => setPackagingPerKit(e.target.value)} className="h-10 text-sm" placeholder="0.00" data-testid={`${prefix}-packaging-per-kit`} />
            <p className="text-xs text-slate-500 mt-1">Multiplied by Purchased Quantity for the Packaging / Processing total.</p>
          </div>
        </div>
        <div className="rounded-lg bg-blue-50/40 border border-blue-100 p-4 self-start">
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-700">Product Cost</span><span className="font-mono text-slate-900">{fmt(num(productCost))}</span></div>
            <div className="flex justify-between"><span className="text-slate-700">Testing Cost</span><span className="font-mono text-slate-900">{fmt(num(testingCost))}</span></div>
            <div className="flex justify-between"><span className="text-slate-700">Shipping / Freight</span><span className="font-mono text-slate-900">{fmt(num(shippingFreight))}</span></div>
            <div className="flex justify-between"><span className="text-slate-700">Misc. Cost</span><span className="font-mono text-slate-900">{fmt(num(miscCost))}</span></div>
            <div className="flex justify-between">
              <span className="text-slate-700">
                Packaging / Processing
                <span className="text-slate-500 text-xs ml-1">({fmt(num(packagingPerKit))} × {q || 0})</span>
              </span>
              <span className="font-mono text-slate-900" data-testid={`${prefix}-packaging-total`}>{fmt(packagingTotal)}</span>
            </div>
          </div>
          <div className="border-t border-blue-200 mt-3 pt-3 flex justify-between text-sm">
            <span className="font-semibold text-slate-900">Total Landed Cost</span>
            <span className="font-mono font-bold text-slate-900" data-testid={`${prefix}-total-landed`}>{fmt(total)}</span>
          </div>
          <div className="mt-3 rounded-md bg-green-50 border border-green-200 px-3 py-3">
            <div className="text-xs font-semibold text-slate-700">Cost Per Kit (CPK)</div>
            <div className="text-3xl font-bold text-green-700 font-mono tracking-tight" data-testid={`${prefix}-cpk`}>${cpk.toFixed(4)}</div>
            <div className="text-xs text-slate-500 mt-1">Based on {q} purchased kits</div>
          </div>
        </div>
      </div>
    </FormCard>
  );
}

function Add10VKForm({ onSaved, onCancel }) {
  const [productName, setProductName] = useState("");
  const [purchasedQuantity, setPurchasedQuantity] = useState("");
  const [kitsInStock, setKitsInStock] = useState("");
  const [touchedStock, setTouchedStock] = useState(false);
  const [batchNumber, setBatchNumber] = useState("");
  const [dateReceived, setDateReceived] = useState(new Date().toISOString().slice(0, 10));
  const [expirationDate, setExpirationDate] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [productCost, setProductCost] = useState("");
  const [testingCost, setTestingCost] = useState("");
  const [shippingFreight, setShippingFreight] = useState("");
  const [miscCost, setMiscCost] = useState("");
  const [packagingPerKit, setPackagingPerKit] = useState("");
  const [testingStatus, setTestingStatus] = useState("untested");
  const [capColor, setCapColor] = useState("");
  const [notes, setNotes] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/inventory/suppliers"); setSuppliers(data?.suppliers || []); } catch { /* silent */ }
    })();
  }, []);

  const onPurchased = (v) => { setPurchasedQuantity(v); if (!touchedStock) setKitsInStock(v); };
  const onStock = (v) => { setKitsInStock(v); setTouchedStock(true); };

  const canSubmit = !!productName.trim() && purchasedQuantity !== "" && Number(purchasedQuantity) >= 0 && kitsInStock !== "" && Number(kitsInStock) >= 0;

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        product_name: productName.trim(),
        kits_in_stock: Number(kitsInStock || 0),
        purchased_quantity: Number(purchasedQuantity || 0),
        testing_status: testingStatus,
        cap_color: capColor || null,
        supplier_name: supplierName || null,
        batch_number: batchNumber || null,
        supplier_notes: notes || null,
        date_received: dateReceived || null,
        expiration_date: expirationDate || null,
        product_cost: productCost === "" ? null : Number(productCost),
        testing_cost: testingCost === "" ? null : Number(testingCost),
        shipping_freight: shippingFreight === "" ? null : Number(shippingFreight),
        misc_cost: miscCost === "" ? null : Number(miscCost),
        packaging_processing_per_kit: packagingPerKit === "" ? null : Number(packagingPerKit),
      };
      await api.post("/admin/inventory/10vk/lots", body);
      toast.success(`10VK inventory saved · ${productName.trim()}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="add-10vk-form">
      <div className={CARD}>
        <div className="flex items-start justify-between px-7 py-6 border-b border-slate-200 flex-wrap gap-3">
          <div>
            <h3 className="text-[22px] leading-tight font-bold text-slate-900 tracking-tight">Add 10VK Inventory</h3>
            <p className="text-sm text-slate-600 mt-1.5">
              IMS is authoritative for 10VK. New lots flow into <b>products.kits_in_stock</b> and update storefront availability immediately.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className={SECONDARY_BTN} data-testid="add-10vk-cancel">Cancel</button>
            <button onClick={submit} disabled={!canSubmit || saving} className={PRIMARY_BTN} data-testid="add-10vk-submit">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={2.5} />}
              {saving ? "Saving…" : "Save Inventory"}
            </button>
          </div>
        </div>

        <div className="p-7 grid grid-cols-1 lg:grid-cols-2 gap-6 bg-slate-50/40">
          <FormCard title="1. Product Information">
            <FieldLabel required>Product</FieldLabel>
            <Input value={productName} onChange={(e) => setProductName(e.target.value)} className="h-10 text-sm" placeholder="e.g. GHK-Cu" data-testid="add-10vk-product-name" autoFocus />
            <p className="text-xs text-slate-500 mt-1">Operator-entered. IMS is authoritative — no auto-populate from catalog.</p>
          </FormCard>

          <FormCard title="2. Inventory Details">
            <FieldLabel required>Purchased Quantity (Kits)</FieldLabel>
            <Input type="number" min="0" value={purchasedQuantity} onChange={(e) => onPurchased(e.target.value)} className="h-10 text-sm" placeholder="0" data-testid="add-10vk-purchased" />
            <p className="text-xs text-slate-500 mt-1">Original kits purchased for this lot. Used as the CPK denominator; does not decrease when inventory is sold.</p>

            <div className="mt-4">
              <FieldLabel required>Actual Stock Quantity (Kits)</FieldLabel>
              <Input type="number" min="0" value={kitsInStock} onChange={(e) => onStock(e.target.value)} className="h-10 text-sm" placeholder="0" data-testid="add-10vk-actual-stock" />
              <p className="text-xs text-slate-500 mt-1">Live physical kit count. Auto-mirrors Purchased until edited. Drives storefront availability.</p>
            </div>

            <div className="mt-4">
              <FieldLabel>Lot / Batch Number</FieldLabel>
              <Input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} className="h-10 text-sm font-mono" placeholder="e.g. 10VK-250918" data-testid="add-10vk-batch" />
            </div>
            <div className="mt-4">
              <FieldLabel>Expiration Date</FieldLabel>
              <Input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} className="h-10 text-sm" data-testid="add-10vk-expiration" />
            </div>
            <div className="mt-4">
              <FieldLabel required>Received Date</FieldLabel>
              <Input type="date" value={dateReceived} onChange={(e) => setDateReceived(e.target.value)} className="h-10 text-sm" data-testid="add-10vk-received" />
            </div>
          </FormCard>

          <FormCard title="3. Supplier">
            <FieldLabel>Supplier</FieldLabel>
            <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} list="ims-10vk-supplier-suggestions" className="h-10 text-sm" placeholder="e.g. OPTIMIZED INSIDER" data-testid="add-10vk-supplier" />
            <datalist id="ims-10vk-supplier-suggestions">
              {suppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
            <p className="text-xs text-slate-500 mt-1">Type a new name or pick from previously used suppliers.</p>
          </FormCard>

          <CostCard
            productCost={productCost} setProductCost={setProductCost}
            testingCost={testingCost} setTestingCost={setTestingCost}
            shippingFreight={shippingFreight} setShippingFreight={setShippingFreight}
            miscCost={miscCost} setMiscCost={setMiscCost}
            packagingPerKit={packagingPerKit} setPackagingPerKit={setPackagingPerKit}
            purchased={purchasedQuantity} prefix="add-10vk"
          />

          <FormCard title="4. Additional Information" subtitle="(Optional)">
            <FieldLabel>Testing Status</FieldLabel>
            <Select value={testingStatus} onValueChange={setTestingStatus}>
              <SelectTrigger className="h-10 text-sm" data-testid="add-10vk-testing"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TESTING_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-4">
              <FieldLabel>Cap Color</FieldLabel>
              <Input value={capColor} onChange={(e) => setCapColor(e.target.value)} className="h-10 text-sm" placeholder="e.g. Red" data-testid="add-10vk-cap" maxLength={40} />
            </div>
            <div className="mt-4">
              <FieldLabel>Notes</FieldLabel>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full min-h-[92px] p-3 text-sm border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y" placeholder="Lead time, QA jots, anything else…" data-testid="add-10vk-notes" maxLength={500} />
              <p className="text-xs text-slate-500 mt-1 text-right">{notes.length}/500</p>
            </div>
          </FormCard>
        </div>

        <div className="mx-7 mb-7 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-start gap-3">
          <Info size={16} className="text-blue-600 shrink-0 mt-0.5" strokeWidth={2.5} />
          <p className="text-sm text-blue-900">
            IMS is authoritative for 10VK. Saving aggregates this lot into <b>products.kits_in_stock</b> and updates storefront availability immediately. Sales deduct kits FIFO (oldest lot first).
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────── Edit dialog ─────────────────────────────── */

function Edit10VKDialog({ lot, onClose, onSaved }) {
  const [productName, setProductName] = useState(lot.name || "");
  const [purchasedQuantity, setPurchasedQuantity] = useState(String(lot.purchased_quantity ?? lot.kits_in_stock ?? ""));
  const [kitsInStock, setKitsInStock] = useState(String(lot.kits_in_stock ?? ""));
  const [batchNumber, setBatchNumber] = useState(lot.batch_number || "");
  const [dateReceived, setDateReceived] = useState(String(lot.date_received || "").slice(0, 10));
  const [expirationDate, setExpirationDate] = useState(String(lot.expiration_date || "").slice(0, 10));
  const [supplierName, setSupplierName] = useState(lot.supplier_name || "");
  const [productCost, setProductCost] = useState(lot.product_cost == null ? "" : String(lot.product_cost));
  const [testingCost, setTestingCost] = useState(lot.testing_cost == null ? "" : String(lot.testing_cost));
  const [shippingFreight, setShippingFreight] = useState(lot.shipping_freight == null ? "" : String(lot.shipping_freight));
  const [miscCost, setMiscCost] = useState(lot.misc_cost == null ? "" : String(lot.misc_cost));
  const [packagingPerKit, setPackagingPerKit] = useState(lot.packaging_processing_per_kit == null ? "" : String(lot.packaging_processing_per_kit));
  const [testingStatus, setTestingStatus] = useState(lot.testing_status || "untested");
  const [capColor, setCapColor] = useState(lot.cap_color || "");
  const [notes, setNotes] = useState(lot.supplier_notes || "");
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/admin/inventory/suppliers"); setSuppliers(data?.suppliers || []); } catch { /* silent */ } })();
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const canSubmit = purchasedQuantity !== "" && Number(purchasedQuantity) >= 0 && kitsInStock !== "" && Number(kitsInStock) >= 0;

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        product_name: productName === "" ? null : productName.trim(),
        kits_in_stock: Number(kitsInStock || 0),
        purchased_quantity: Number(purchasedQuantity || 0),
        testing_status: testingStatus,
        cap_color: capColor,
        supplier_name: supplierName,
        batch_number: batchNumber,
        supplier_notes: notes,
        date_received: dateReceived,
        expiration_date: expirationDate,
        product_cost: productCost === "" ? null : Number(productCost),
        testing_cost: testingCost === "" ? null : Number(testingCost),
        shipping_freight: shippingFreight === "" ? null : Number(shippingFreight),
        misc_cost: miscCost === "" ? null : Number(miscCost),
        packaging_processing_per_kit: packagingPerKit === "" ? null : Number(packagingPerKit),
      };
      await api.put(`/admin/inventory/10vk/lots/${lot.lot_id}`, body);
      toast.success("Updated · 10VK lot");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 backdrop-blur-sm px-4 py-8" onClick={onClose} data-testid="edit-10vk-dialog">
      <div className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl border border-slate-200 my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-7 py-6 border-b border-slate-200 flex-wrap gap-3 bg-white rounded-t-xl">
          <div>
            <h3 className="text-[22px] leading-tight font-bold text-slate-900 tracking-tight">Edit 10VK Inventory</h3>
            <p className="text-sm text-slate-600 mt-1.5">IMS is authoritative. Changes re-aggregate into <b>products.kits_in_stock</b> and update storefront availability.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className={SECONDARY_BTN} data-testid="edit-10vk-cancel">Cancel</button>
            <button onClick={submit} disabled={!canSubmit || saving} className={PRIMARY_BTN} data-testid="edit-10vk-submit">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} strokeWidth={2.5} />}
              {saving ? "Saving…" : "Save Inventory"}
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors ml-1" data-testid="edit-10vk-close" aria-label="Close"><X size={18} /></button>
          </div>
        </div>

        <div className="p-7 grid grid-cols-1 lg:grid-cols-2 gap-6 bg-slate-50/40">
          <FormCard title="1. Product Information">
            <FieldLabel required>Product name</FieldLabel>
            <Input value={productName} onChange={(e) => setProductName(e.target.value)} className="h-10 text-sm" data-testid="edit-10vk-product-name" />
          </FormCard>

          <FormCard title="2. Inventory Details">
            <FieldLabel required>Purchased Quantity (Kits)</FieldLabel>
            <Input type="number" min="0" value={purchasedQuantity} onChange={(e) => setPurchasedQuantity(e.target.value)} className="h-10 text-sm" data-testid="edit-10vk-purchased" />
            <p className="text-xs text-slate-500 mt-1">Acquisition kits used for CPK. Independent of Actual Stock.</p>
            <div className="mt-4">
              <FieldLabel required>Actual Stock Quantity (Kits)</FieldLabel>
              <Input type="number" min="0" value={kitsInStock} onChange={(e) => setKitsInStock(e.target.value)} className="h-10 text-sm" data-testid="edit-10vk-actual-stock" />
              <p className="text-xs text-slate-500 mt-1">Physical kit count. Editable; does NOT change CPK or storefront.</p>
            </div>
            <div className="mt-4"><FieldLabel>Lot / Batch Number</FieldLabel><Input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} className="h-10 text-sm font-mono" data-testid="edit-10vk-batch" /></div>
            <div className="mt-4"><FieldLabel>Expiration Date</FieldLabel><Input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} className="h-10 text-sm" data-testid="edit-10vk-expiration" /></div>
            <div className="mt-4"><FieldLabel required>Received Date</FieldLabel><Input type="date" value={dateReceived} onChange={(e) => setDateReceived(e.target.value)} className="h-10 text-sm" data-testid="edit-10vk-received" /></div>
          </FormCard>

          <FormCard title="3. Supplier">
            <FieldLabel required>Supplier</FieldLabel>
            <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} list="ims-10vk-supplier-suggestions-edit" className="h-10 text-sm" placeholder="e.g. OPTIMIZED INSIDER" data-testid="edit-10vk-supplier" />
            <datalist id="ims-10vk-supplier-suggestions-edit">
              {suppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
          </FormCard>

          <CostCard
            productCost={productCost} setProductCost={setProductCost}
            testingCost={testingCost} setTestingCost={setTestingCost}
            shippingFreight={shippingFreight} setShippingFreight={setShippingFreight}
            miscCost={miscCost} setMiscCost={setMiscCost}
            packagingPerKit={packagingPerKit} setPackagingPerKit={setPackagingPerKit}
            purchased={purchasedQuantity} prefix="edit-10vk"
          />

          <FormCard title="4. Additional Information" subtitle="(Optional)">
            <FieldLabel>Testing Status</FieldLabel>
            <Select value={testingStatus} onValueChange={setTestingStatus}>
              <SelectTrigger className="h-10 text-sm" data-testid="edit-10vk-testing"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TESTING_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-4">
              <FieldLabel>Cap Color</FieldLabel>
              <Input value={capColor} onChange={(e) => setCapColor(e.target.value)} className="h-10 text-sm" placeholder="e.g. Red" data-testid="edit-10vk-cap" maxLength={40} />
            </div>
            <div className="mt-4">
              <FieldLabel>Notes</FieldLabel>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full min-h-[92px] p-3 text-sm border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y" data-testid="edit-10vk-notes" maxLength={500} />
            </div>
          </FormCard>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────── Shared small components ───────────────────── */

function FormCard({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <h4 className="text-[15px] font-bold text-slate-900 mb-5 pb-3 border-b border-slate-100 tracking-tight">
        {title}
        {subtitle && <span className="text-slate-400 font-medium ml-1.5 text-sm">{subtitle}</span>}
      </h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function FieldLabel({ required, children }) {
  return <label className="block text-[13px] font-semibold text-slate-800 mb-1.5">{children}{required && <span className="text-red-500 ml-0.5">*</span>}</label>;
}
